import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { API_URL } from './AdminShared';
import { Send, RotateCcw, Calculator } from 'lucide-react';

export const AIAccountantTab = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `acc_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/admin/ai-assistant`, {
        message: msg,
        type: 'accountant',
        session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please try again.', error: true }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const resetChat = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/ai-assistant/reset`, {
        type: 'accountant',
        session_id: sessionId,
      });
    } catch { /* ignore */ }
    setMessages([]);
  };

  const quickPrompts = [
    "What's my total revenue breakdown?",
    "Give me a monthly financial summary",
    "What UK tax obligations do I have?",
    "Analyse my cash flow and suggest improvements",
    "What's my VAT position?",
  ];

  return (
    <div className="luxury-card overflow-hidden" data-testid="ai-accountant-tab">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E07A3A]/20 flex items-center justify-center">
            <Calculator className="h-5 w-5 text-[#E07A3A]" />
          </div>
          <div>
            <h3 className="text-white font-['Playfair_Display'] text-lg">AI Accountant</h3>
            <p className="text-slate-400 text-xs">Financial guidance & tax insights</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={resetChat} className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="accountant-reset-btn">
          <RotateCcw className="h-4 w-4 mr-1" /> New Chat
        </Button>
      </div>

      {/* Chat Area */}
      <div className="h-[500px] overflow-y-auto p-6 space-y-4 bg-[#FAFBFC]" data-testid="accountant-chat-area">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Calculator className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500 text-sm mb-1">Ask me anything about your finances</p>
            <p className="text-slate-400 text-xs mb-6">Revenue, expenses, tax guidance, financial summaries</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => { setInput(prompt); inputRef.current?.focus(); }}
                  className="px-3 py-1.5 text-xs bg-white border border-slate-200 text-slate-600 hover:border-[#E07A3A] hover:text-[#E07A3A] transition-colors rounded-full"
                  data-testid={`accountant-quick-${prompt.slice(0, 10)}`}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={`${msg.role}-${i}-${msg.text.slice(0, 20)}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-[#1A1A1A] text-white rounded-t-2xl rounded-bl-2xl'
                : msg.error
                  ? 'bg-red-50 text-red-700 border border-red-200 rounded-t-2xl rounded-br-2xl'
                  : 'bg-white text-slate-700 border border-slate-200 rounded-t-2xl rounded-br-2xl shadow-sm'
            }`} data-testid={`accountant-msg-${i}`}>
              <div className="whitespace-pre-wrap">{msg.text}</div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-t-2xl rounded-br-2xl px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 bg-[#E07A3A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#E07A3A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#E07A3A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-slate-200 p-4 bg-white">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask about revenue, tax, expenses..."
            className="flex-1 border border-slate-200 px-4 py-2.5 text-sm focus:border-[#E07A3A] focus:outline-none rounded-lg"
            disabled={loading}
            data-testid="accountant-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-[#E07A3A] hover:bg-[#C4652E] text-white px-4 rounded-lg"
            data-testid="accountant-send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-center">AI guidance only — consult a qualified accountant for formal advice</p>
      </div>
    </div>
  );
};
